import { useCallback, useMemo, useReducer } from 'react';
import { isNumber, noop, binarySearch } from '@chkp/mwc-common';

const useVirtualizedRows = (rowHeight, rowsCount) => {
	const isVariableSize = !isNumber(rowHeight);
	const [$resetRowHeights, resetRowHeights] = useReducer((s) => s + 1, 0);

	const rowHeights = useMemo(() => {
		noop($resetRowHeights);
		if (isVariableSize) {
			const heights = new Uint16Array(rowsCount);
			for (let i = 0; i < rowsCount; i++) {
				heights[i] = rowHeight(i);
			}
			return heights;
		}
		return null;
	}, [$resetRowHeights, isVariableSize, rowHeight, rowsCount]);

	const rowPositions = useMemo(() => {
		if (isVariableSize) {
			const positions = new Uint32Array(rowsCount + 1);
			let nextPosition = 0;
			for (let i = 0; i < rowsCount; i++) {
				positions[i] = nextPosition;
				nextPosition += rowHeights[i];
			}
			return positions;
		}
		return null;
	}, [isVariableSize, rowHeights, rowsCount]);

	const totalHeight = rowsCount
		? isVariableSize
			? rowPositions[rowsCount - 1] + rowHeights[rowsCount - 1]
			: rowsCount * rowHeight
		: 0;

	const getRowPosition = useCallback(
		(idx) => (isVariableSize ? rowPositions[idx] : idx * rowHeight),
		[isVariableSize, rowHeight, rowPositions],
	);

	const itemSize = useMemo(
		() => (isVariableSize ? (idx) => rowHeights[idx] : rowHeight),
		[isVariableSize, rowHeight, rowHeights],
	);

	// const estimatedItemSize = useMemo(
	// 	() => (isVariableSize
	// 		? rowPositions[rowPositions.length - 1] / rowsCount
	// 		: rowHeight),
	// 	[isVariableSize, rowHeight, rowPositions, rowsCount],
	// );

	const getIndexAt = useCallback(
		(top) =>
			isVariableSize
				? Math.min(binarySearch(rowPositions, top), rowsCount - 1)
				: Math.floor(top / rowHeight),
		[isVariableSize, rowHeight, rowPositions, rowsCount],
	);

	return {
		getRowPosition,
		itemSize,
		// estimatedItemSize,
		resetRowHeights,
		getIndexAt,
		rowPositions,
		isVariableSize,
		totalHeight,
	};
};

export { useVirtualizedRows };
